import { saveBlob } from '../../restricted/broadcast/broadcastExport.js';
import { columnValue, exportColumns } from './piColumns.js';
import { BAND_OPTIONS, CONTRACT_OPTIONS, MIN_COMPANY_SEARCH, SIGNAL_DROP, cleanCompanyFilters } from './piCompanyFilters.js';
import { classifyError, errorMessage } from './piEnvelope.js';
import { formatDateFull, formatInteger, isNumber } from './piFormat.js';
import { glossaryEntry, glossaryText } from './glossary/index.js';
import {
  COHORT_OPTIONS, ORIGIN_OPTIONS, PLAN_OPTIONS, TRIAL_OPTIONS, UNIVERSE_OPTIONS, cleanOnboardingFilters, previousSalePeriod, resolveSalePeriod,
  statusOptionsFor,
} from './piOnboardingFilters.js';
import {
  GLOBAL_FIELDS, GRANULARITY_OPTIONS, JOURNEY_OPTIONS, MODULE_OPTIONS, PLATFORM_OPTIONS, ROLE_CLASS_OPTIONS, SIZE_BAND_FILTER_OPTIONS,
  compareEnabled, ignoredFilters, optionLabel, previousPeriod, resolvePeriod, sanitizeFilters,
} from './piQuery.js';
import { coverageGroups, listOf, objectOf } from './piRequests.js';
import { envelopeStatus, showsValue, stateLabel } from './piState.js';
import { guardedMetric, numberOf } from './piTable.js';
import { FORMULA_PREFIX, brasiliaText, buildXlsx, sheetNames } from './piXlsx.js';

export const EXPORT_LIMIT = 50000;

export const EXPORT_FORMATS = [
  { value: 'xlsx', label: 'Excel (.xlsx)' },
  { value: 'csv', label: 'CSV' },
];

export const ABOUT_SHEET = 'Sobre';

export const PARTIAL_MARK = 'PARCIAL';

export const EXPORT_UNSUPPORTED = 'export-unsupported';

export const EXPORT_UNSUPPORTED_TEXT = 'A exportação completa ainda não está disponível no servidor do PI (atualize o MarQ.Api).';

export const STATE_COLUMN_SUFFIX = ' — estado';

export const CSV_MIME = 'text/csv;charset=utf-8;';

export const MASTER_USER_KEY = 'mamUserMasterInfo';

export const BRASILIA_SUFFIX = ' (horário de Brasília)';

const BOM = '﻿';
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const NUMERIC_TYPES = ['int', 'decimal', 'percent'];
const ABOUT_COLUMNS = [
  { label: 'Informação', type: 'text', width: 26 },
  { label: 'Valor', type: 'text', width: 60, wrap: true },
];

const textOf = (value) => (value === null || value === undefined ? '' : String(value).trim());

const has = (list, value) => list.indexOf(value) !== -1;

const plural = (count, one, many) => formatInteger(count) + ' ' + (count === 1 ? one : many);

export const masterUserName = (storage) => {
  try {
    const info = JSON.parse(storage.getItem(MASTER_USER_KEY) || 'null');
    return info && typeof info.name === 'string' ? info.name.trim() : '';
  } catch (e) {
    return '';
  }
};

export const screenLink = (origin, fullPath) => {
  const path = typeof fullPath === 'string' ? fullPath : '';
  if (path.charAt(0) !== '/') return '';
  return (typeof origin === 'string' ? origin.replace(/\/+$/, '') : '') + path;
};

export const browserUserName = () => {
  try {
    return typeof window === 'undefined' ? '' : masterUserName(window.localStorage);
  } catch (e) {
    return '';
  }
};

export const browserLink = (route) => {
  try {
    if (typeof window === 'undefined' || !window.location) return '';
    return screenLink(window.location.origin, route && typeof route.fullPath === 'string' ? route.fullPath : '');
  } catch (e) {
    return '';
  }
};

export const slugOf = (value) => textOf(value)
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const compactDate = (iso) => (DATE_ONLY.test(textOf(iso)) ? textOf(iso).replace(/-/g, '') : '');

const pad2 = (value) => (value < 10 ? '0' : '') + value;

const brasiliaStamp = (now) => {
  const date = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  const shifted = new Date(date.getTime() - 3 * 3600000);
  return shifted.getUTCFullYear() + pad2(shifted.getUTCMonth() + 1) + pad2(shifted.getUTCDate())
    + '-' + pad2(shifted.getUTCHours()) + pad2(shifted.getUTCMinutes());
};

export const exportFileName = (options) => {
  const settings = options || {};
  const period = objectOf(settings.period);
  const from = compactDate(period.from);
  const to = compactDate(period.to);
  const parts = ['pi', slugOf(settings.screen), slugOf(settings.table)].filter((part) => !!part);
  return parts.join('-')
    + (from && to ? '-' + from + '_a_' + to : '')
    + '-gerado-' + brasiliaStamp(settings.now)
    + (settings.partial ? '-' + PARTIAL_MARK : '')
    + (settings.format === 'csv' ? '.csv' : '.xlsx');
};

export const exportPeriod = (filters, onboarding, now) => {
  const reference = now instanceof Date ? now : new Date();
  const period = onboarding === true
    ? resolveSalePeriod(cleanOnboardingFilters(filters), reference)
    : resolvePeriod(sanitizeFilters(filters), reference);
  return { from: period.from, to: period.to };
};

const periodText = (period) => formatDateFull(period.from) + ' a ' + formatDateFull(period.to);

const globalLines = (filters, fields, settings, now) => {
  const clean = sanitizeFilters(filters);
  const lines = [];
  GLOBAL_FIELDS.forEach((field) => {
    if (!has(fields, field)) return;
    if (field === 'period' && settings.onboarding !== true) lines.push('Período: ' + periodText(resolvePeriod(clean, now)));
    else if (field === 'compare') {
      const previous = settings.onboarding === true
        ? previousSalePeriod(resolveSalePeriod(cleanOnboardingFilters(filters), now))
        : previousPeriod(resolvePeriod(clean, now));
      lines.push(compareEnabled(clean)
        ? 'Comparar com o período anterior: sim' + (previous ? ' (' + periodText(previous) + ')' : '')
        : 'Comparar com o período anterior: não');
    } else if (field === 'company' && clean.companyId) lines.push('Empresa: ' + (clean.companyName || clean.companyId));
    else if (field === 'module' && clean.module) lines.push('Módulo: ' + optionLabel(MODULE_OPTIONS, clean.module));
    else if (field === 'featureType' && clean.featureType) lines.push('Funcionalidade: ' + (textOf(settings.featureName) || clean.featureType));
    else if (field === 'roleClass' && clean.roleClass) lines.push('Papel do usuário: ' + optionLabel(ROLE_CLASS_OPTIONS, clean.roleClass));
    else if (field === 'platform' && clean.platform) lines.push('Plataforma: ' + optionLabel(PLATFORM_OPTIONS, clean.platform));
    else if (field === 'sizeBand' && clean.sizeBand) lines.push('Porte (funcionários): ' + optionLabel(SIZE_BAND_FILTER_OPTIONS, clean.sizeBand));
    else if (field === 'segment' && clean.segment) lines.push('Perfil da empresa: ' + clean.segment);
    else if (field === 'granularity') lines.push('Granularidade: ' + optionLabel(GRANULARITY_OPTIONS, clean.granularity || 'day'));
  });
  return lines;
};

const companyLines = (value) => {
  const clean = cleanCompanyFilters(value);
  const lines = [];
  if (clean.search.length >= MIN_COMPANY_SEARCH) lines.push('Buscar empresa: "' + clean.search + '"');
  if (clean.band) lines.push('Faixa de ativos (30 dias): ' + optionLabel(BAND_OPTIONS, clean.band));
  if (clean.journey) lines.push('Jornada: ' + optionLabel(JOURNEY_OPTIONS, clean.journey));
  if (clean.contract) lines.push('Contrato: ' + optionLabel(CONTRACT_OPTIONS, clean.contract));
  if (clean.signal === SIGNAL_DROP) lines.push('Só com queda de atividade');
  return lines;
};

const onboardingLines = (filters, now) => {
  const clean = cleanOnboardingFilters(filters);
  const lines = ['Vendas de: ' + periodText(resolveSalePeriod(clean, now))];
  lines.push('Coorte: ' + optionLabel(COHORT_OPTIONS, clean.cohort || 'month'));
  lines.push('Universo: ' + optionLabel(UNIVERSE_OPTIONS, clean.universe || 'sold'));
  if (clean.status) lines.push('Situação hoje: ' + optionLabel(statusOptionsFor(clean.universe), clean.status));
  if (clean.trial) lines.push('Trial antes da venda: ' + optionLabel(TRIAL_OPTIONS, clean.trial));
  if (clean.started) lines.push('Só as que iniciaram o uso');
  if (clean.plan) lines.push('Plano (hoje): ' + optionLabel(PLAN_OPTIONS, clean.plan));
  if (clean.journeyNow) lines.push('Jornada (hoje): ' + optionLabel(JOURNEY_OPTIONS, clean.journeyNow));
  if (clean.origin) lines.push('Origem: ' + optionLabel(ORIGIN_OPTIONS, clean.origin));
  if (clean.shard) lines.push('Banco (shard): ' + clean.shard);
  return lines;
};

export const filterLines = (options) => {
  const settings = options || {};
  const filters = objectOf(settings.filters);
  const fields = Array.isArray(settings.fields) ? settings.fields : [];
  const now = settings.now instanceof Date ? settings.now : new Date();
  let lines = [];
  if (settings.onboarding === true) lines = lines.concat(onboardingLines(filters, now));
  lines = lines.concat(globalLines(filters, fields, settings, now));
  if (settings.company) lines = lines.concat(companyLines(settings.company));
  listOf(settings.extra).forEach((line) => {
    if (textOf(line)) lines.push(textOf(line));
  });
  if (fields.length) {
    const ignored = ignoredFilters(filters, fields);
    if (ignored.length) lines.push('Não se aplica a esta visão: ' + ignored.join(' · '));
  }
  return lines;
};

const metricType = (unit) => {
  if (unit === 'pct') return 'percent';
  if (unit === 'ratio' || unit === 'days') return 'decimal';
  return 'int';
};

const RATIO_DECIMALS = 2;

const plainValue = (value, type) => {
  if (has(NUMERIC_TYPES, type)) return numberOf(value);
  if (type === 'date' || type === 'datetime') {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    return textOf(value) ? textOf(value) : null;
  }
  if (value === null || value === undefined) return '';
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  if (Array.isArray(value)) return value.map(textOf).filter((item) => !!item).join(', ');
  if (typeof value === 'object') return '';
  return String(value);
};

const metricColumns = (spec, rows) => {
  const metrics = rows.map((row) => guardedMetric(columnValue(spec, row)));
  const sample = metrics.filter((metric) => metric.value !== null)[0] || metrics[0];
  const unit = spec.unit || (sample ? sample.unit : 'count');
  const columns = [{
    key: spec.key,
    label: spec.label,
    type: metricType(unit),
    decimals: unit === 'ratio' ? RATIO_DECIMALS : undefined,
    infoKey: spec.infoKey || '',
    width: spec.width,
    values: metrics.map((metric) => (showsValue(metric) ? metric.value : null)),
  }];
  if (metrics.some((metric) => metric.state !== 'ok')) {
    columns.push({
      key: spec.key + '.estado',
      label: spec.label + STATE_COLUMN_SUFFIX,
      type: 'text',
      infoKey: '',
      values: metrics.map((metric) => (metric.state === 'ok' ? '' : stateLabel(metric.state))),
    });
  }
  return columns;
};

export const exportTableData = (specs, rows) => {
  const list = Array.isArray(rows) ? rows : [];
  let columns = [];
  exportColumns(specs).forEach((spec) => {
    if (spec.type === 'metric') {
      columns = columns.concat(metricColumns(spec, list));
      return;
    }
    columns.push({
      key: spec.key,
      label: spec.label,
      type: spec.type,
      decimals: spec.decimals,
      infoKey: spec.infoKey || '',
      width: spec.width,
      values: list.map((row) => plainValue(columnValue(spec, row), spec.type)),
    });
  });
  return {
    columns: columns.map((column) => {
      const out = { key: column.key, label: column.label, type: column.type, infoKey: column.infoKey, width: column.width };
      if (isNumber(column.decimals)) out.decimals = column.decimals;
      return out;
    }),
    rows: list.map((row, index) => columns.map((column) => column.values[index])),
  };
};

export const csvNumber = (value) => {
  const rounded = Math.round(value * 1000000) / 1000000;
  return String(rounded === 0 ? 0 : rounded).replace('.', ',');
};

const guardText = (text) => (FORMULA_PREFIX.test(text) ? "'" + text : text);

export const csvCell = (value, type) => {
  if (value === null || value === undefined || value === '') return '';
  if (has(NUMERIC_TYPES, type) && isNumber(value)) return csvNumber(value);
  if (type === 'date' || type === 'datetime') {
    const text = brasiliaText(value, type === 'datetime');
    if (text) return text;
  }
  if (typeof value === 'number' && !isNumber(value)) return '';
  return guardText(value instanceof Date ? brasiliaText(value, true) : String(value));
};

export const csvHeader = (column) => (column.type === 'percent' ? column.label + ' (%)' : column.label);

const quote = (text) => '"' + String(text).replace(/"/g, '""') + '"';

export const csvText = (data) => {
  const columns = data.columns;
  const lines = [columns.map((column) => quote(guardText(csvHeader(column)))).join(';')];
  data.rows.forEach((row) => {
    lines.push(columns.map((column, index) => quote(csvCell(row[index], column.type))).join(';'));
  });
  return lines.join('\r\n');
};

const xlsxSheet = (name, data) => ({
  name: name,
  columns: data.columns.map((column) => ({ label: column.label, type: column.type, width: column.width, decimals: column.decimals })),
  rows: data.rows.map((row) => row.map((value, index) => (data.columns[index].type === 'percent' && isNumber(value) ? value / 100 : value))),
});

const asOfText = (envelope) => {
  const status = envelopeStatus(envelope);
  const text = status.asOf ? brasiliaText(status.asOf, true) : '';
  return text ? text + BRASILIA_SUFFIX : 'não informado';
};

const tableRows = (table) => {
  const status = envelopeStatus(table.envelope);
  const rows = [];
  rows.push(['Dados de', asOfText(table.envelope)]);
  rows.push(['Dados parciais', status.partial ? 'Sim: há dia que ainda não fechou; os números podem mudar' : 'Não']);
  rows.push(['Versão das definições', status.definitionVersion !== null ? 'v' + status.definitionVersion : 'não informada']);
  coverageGroups([table.envelope]).forEach((group) => rows.push(['Cobertura', group.text]));
  status.notes.forEach((note) => rows.push(['Nota', note]));
  rows.push(['Linhas exportadas', { value: table.exportedRows, type: 'int' }]);
  rows.push(['Total do recorte', { value: table.total, type: 'int' }]);
  rows.push(['Exportação parcial', table.truncated
    ? 'Sim: só as primeiras ' + plural(table.exportedRows, 'linha', 'linhas') + ' de ' + formatInteger(table.total) + ', na ordem da tela (limite de '
      + formatInteger(EXPORT_LIMIT) + ' por exportação)'
    : 'Não']);
  return rows;
};

const definitionRows = (tables) => {
  const rows = [];
  const seen = [];
  tables.forEach((table) => {
    table.data.columns.forEach((column) => {
      const entry = column.infoKey ? glossaryEntry(column.infoKey) : null;
      const mark = column.label + '|' + column.infoKey;
      if (!entry || has(seen, mark)) return;
      seen.push(mark);
      rows.push([column.label, glossaryText(entry, { version: envelopeStatus(table.envelope).definitionVersion })]);
    });
  });
  return rows;
};

export const aboutRows = (options) => {
  const settings = options || {};
  const tables = listOf(settings.tables);
  const now = settings.now instanceof Date ? settings.now : new Date();
  let rows = [['Tela', textOf(settings.screen)]];
  if (tables.length === 1) rows.push(['Tabela', textOf(tables[0].title)]);
  if (textOf(settings.link)) rows.push(['Link da tela', textOf(settings.link)]);
  listOf(settings.filters).forEach((line) => {
    if (textOf(line)) rows.push(['Filtro', textOf(line)]);
  });
  tables.forEach((table) => {
    if (tables.length > 1) rows.push(['Tabela', textOf(table.title)]);
    rows = rows.concat(tableRows(table));
  });
  rows.push(['Gerado por', textOf(settings.generatedBy) || 'não identificado']);
  rows.push(['Gerado em', brasiliaText(now, true) + BRASILIA_SUFFIX]);
  const definitions = definitionRows(tables);
  if (definitions.length) {
    rows.push([]);
    rows.push(['Definições das colunas', '']);
    rows = rows.concat(definitions);
  }
  return rows;
};

const aboutSheet = (settings, tables) => ({
  name: ABOUT_SHEET,
  columns: ABOUT_COLUMNS,
  rows: aboutRows(Object.assign({}, settings, { tables: tables })),
  freeze: false,
  autofilter: false,
});

const tableOf = (source, fallbackTitle) => {
  const settings = source || {};
  const data = exportTableData(settings.columns, settings.rows);
  const exportedRows = data.rows.length;
  const total = isNumber(settings.total) && settings.total > exportedRows ? Math.floor(settings.total) : exportedRows;
  return {
    title: textOf(settings.title) || textOf(fallbackTitle) || 'Tabela',
    data: data,
    envelope: settings.envelope || null,
    exportedRows: exportedRows,
    total: total,
    truncated: settings.truncated === true || total > exportedRows,
  };
};

export const buildTableExport = (options) => {
  const settings = options || {};
  const format = settings.format === 'csv' ? 'csv' : 'xlsx';
  const now = settings.now instanceof Date ? settings.now : new Date();
  const table = tableOf({
    title: settings.table,
    columns: settings.columns,
    rows: settings.rows,
    envelope: settings.envelope,
    total: settings.total,
    truncated: settings.truncated,
  });
  const fileName = exportFileName({
    screen: settings.screen, table: table.title, period: settings.period, format: format, partial: table.truncated, now: now,
  });
  const result = { format: format, fileName: fileName, exportedRows: table.exportedRows, total: table.total, truncated: table.truncated };
  if (format === 'csv') {
    const text = csvText(table.data);
    return Object.assign(result, { text: text, blob: new Blob([BOM + text], { type: CSV_MIME }) });
  }
  const names = sheetNames([ABOUT_SHEET, table.title]);
  const sheets = [aboutSheet(Object.assign({}, settings, { now: now }), [table]), xlsxSheet(names[1], table.data)];
  return Object.assign(result, { sheets: sheets, blob: buildXlsx(sheets, { now: now, activeSheet: 1 }) });
};

export const buildScreenExport = (options) => {
  const settings = options || {};
  const now = settings.now instanceof Date ? settings.now : new Date();
  const tables = listOf(settings.tables).map((table, index) => tableOf(table, 'Tabela ' + (index + 1)));
  const truncated = tables.some((table) => table.truncated);
  const names = sheetNames([ABOUT_SHEET].concat(tables.map((table) => table.title)));
  const sheets = [aboutSheet(Object.assign({}, settings, { now: now }), tables)]
    .concat(tables.map((table, index) => xlsxSheet(names[index + 1], table.data)));
  return {
    format: 'xlsx',
    fileName: exportFileName({ screen: settings.screen, period: settings.period, format: 'xlsx', partial: truncated, now: now }),
    exportedRows: tables.reduce((sum, table) => sum + table.exportedRows, 0),
    tables: tables.map((table) => ({ title: table.title, exportedRows: table.exportedRows, total: table.total, truncated: table.truncated })),
    truncated: truncated,
    sheets: sheets,
    blob: buildXlsx(sheets, { now: now, activeSheet: tables.length ? 1 : 0 }),
  };
};

export const exportTable = (options) => {
  const result = buildTableExport(options);
  saveBlob(result.blob, result.fileName);
  return result;
};

export const exportScreen = (options) => {
  const result = buildScreenExport(options);
  saveBlob(result.blob, result.fileName);
  return result;
};

export const exportQuery = (query) => {
  const source = objectOf(query);
  const next = {};
  Object.keys(source).forEach((key) => {
    if (key !== 'page' && key !== 'pageSize' && key !== 'export') next[key] = source[key];
  });
  next.export = 'true';
  return next;
};

export const exportPage = (envelope, pick) => {
  const data = envelope ? envelope.data : null;
  const holder = objectOf(typeof pick === 'function' ? pick(data) : data);
  let rows = [];
  if (Array.isArray(holder.rows)) rows = holder.rows;
  else if (Array.isArray(holder.items)) rows = holder.items;
  const total = isNumber(holder.total) && holder.total >= rows.length ? Math.floor(holder.total) : rows.length;
  if (total > rows.length && !isNumber(holder.exportedRows)) {
    const error = new Error(EXPORT_UNSUPPORTED_TEXT);
    error.kind = EXPORT_UNSUPPORTED;
    throw error;
  }
  return {
    rows: rows,
    total: total,
    exportedRows: rows.length,
    truncated: holder.truncated === true || total > rows.length,
    envelope: envelope || null,
  };
};

export const inlinePage = (rows, envelope, total) => {
  const list = Array.isArray(rows) ? rows : [];
  const kept = list.length > EXPORT_LIMIT ? list.slice(0, EXPORT_LIMIT) : list;
  const count = isNumber(total) && total > list.length ? Math.floor(total) : list.length;
  return { rows: kept, total: count, exportedRows: kept.length, truncated: count > kept.length, envelope: envelope || null };
};

export const loadedPage = (value) => {
  const source = objectOf(value);
  const page = inlinePage(source.rows, source.envelope, source.total);
  if (source.truncated === true) page.truncated = true;
  return page;
};

export const mappedPage = (page, map) => {
  if (typeof map !== 'function') return page;
  const rows = map(page.rows, page.envelope);
  return Object.assign({}, page, { rows: Array.isArray(rows) ? rows : [] });
};

export const partialQuestion = (total, exported) => {
  if (isNumber(total) && isNumber(exported) && total > exported) {
    return 'O recorte tem ' + formatInteger(total) + ' linhas e o limite é ' + formatInteger(EXPORT_LIMIT) + '. Baixar as primeiras '
      + formatInteger(exported) + ' na ordem da tela?';
  }
  return 'O recorte passa do limite de ' + formatInteger(EXPORT_LIMIT) + ' linhas. Baixar as primeiras ' + formatInteger(EXPORT_LIMIT)
    + ' na ordem da tela?';
};

export const screenPartialQuestion = (tables) => {
  const partial = listOf(tables).filter((table) => table.truncated);
  if (!partial.length) return '';
  if (partial.length === 1) {
    return 'A tabela "' + textOf(partial[0].title) + '" ' + partialQuestion(partial[0].total, partial[0].exportedRows).replace('O recorte ', '');
  }
  const names = partial.map((table) => '"' + textOf(table.title) + '" (' + plural(table.total, 'linha', 'linhas') + ')');
  return 'As tabelas ' + names.slice(0, -1).join(', ') + ' e ' + names[names.length - 1] + ' passam do limite de ' + formatInteger(EXPORT_LIMIT)
    + ' linhas. Baixar as primeiras ' + formatInteger(EXPORT_LIMIT) + ' de cada uma, na ordem da tela?';
};

export const exportNotice = (rows) => 'Planilha com ' + plural(isNumber(rows) ? rows : 0, 'linha', 'linhas') + ' gerada.';

export const screenNotice = (tables, rows) => 'Planilha com ' + plural(isNumber(tables) ? tables : 0, 'tabela', 'tabelas') + ' e '
  + plural(isNumber(rows) ? rows : 0, 'linha', 'linhas') + ' gerada.';

export const exportErrorMessage = (error, title) => {
  if (error && error.kind === EXPORT_UNSUPPORTED) return EXPORT_UNSUPPORTED_TEXT;
  const info = classifyError(error);
  if (info.kind === 'failed' && (info.status === 504 || info.status === 408)) {
    return 'Tempo esgotado ao montar a exportação' + (textOf(title) ? ' de "' + textOf(title) + '"' : '') + '. Refine o recorte (período ou filtros) e tente de novo.';
  }
  return errorMessage(error, textOf(title) ? 'exportar a tabela "' + textOf(title) + '"' : 'exportar a tabela');
};

export const createExportRegistry = () => {
  const entries = [];
  const listeners = [];
  const notify = () => listeners.slice().forEach((listener) => listener(entries.length));
  return {
    register(entry) {
      if (!entry || typeof entry.load !== 'function') return () => {};
      entries.push(entry);
      notify();
      return () => {
        const index = entries.indexOf(entry);
        if (index === -1) return;
        entries.splice(index, 1);
        notify();
      };
    },
    entries() {
      const list = entries.slice();
      return list.sort((a, b) => {
        const left = a.el;
        const right = b.el;
        if (!left || !right || typeof left.compareDocumentPosition !== 'function' || left === right) return 0;
        return left.compareDocumentPosition(right) & 4 ? -1 : 1;
      });
    },
    count() {
      return entries.length;
    },
    subscribe(listener) {
      if (typeof listener !== 'function') return () => {};
      listeners.push(listener);
      return () => {
        const index = listeners.indexOf(listener);
        if (index !== -1) listeners.splice(index, 1);
      };
    },
  };
};

export const EXPORT_REGISTRY_KEY = 'piExportRegistry';

export const exportProvide = () => {
  const provided = {};
  provided[EXPORT_REGISTRY_KEY] = createExportRegistry();
  return provided;
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piExport.js